import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import isEmail from 'validator/lib/isEmail';
import accountApi from '../../account/api/accountApi';
import AccountRoles from '../../account/enums/AccountRoles';
import Dialog from '../../common/component/form/Dialog';
import Dropdown from '../../common/component/form/Dropdown';
import TextInput from '../../common/component/form/TextInput';
import Role from '../../common/enums/Role';
import {checkValidation} from '../../common/util/ValidationUtil';
import ErrorCode from '../../common/enums/ErrorCode';
import {useSelector} from 'react-redux';

export default function AccountDialog({
                                          clubId,
                                          title,
                                          open,
                                          onSave,
                                          onCancel,
                                          selectableRoles,
                                          account: initialAccount
                                      }) {
    const [t] = useTranslation();
    const accountId = useSelector(state => state.account.id);

    const roles = selectableRoles
        .filter((role) => role !== AccountRoles.ADMINISTRATOR)
        .map((role) => {
                return {
                    value: role,
                    label: t(`Role.${role}`)
                };
            }
        );

    const [account, setAccount] = useState(null);
    const [repeatedPassword, setRepeatedPassword] = useState(undefined);
    const [validation, setValidation] = useState();
    const [isValidated, setIsValidated] = useState(false);
    const [accountAlreadyExistsError, setAccountAlreadyExistsError] = useState(false);

    useEffect(() => {
        if (open) {
            let initialRole;
            if (selectableRoles.length === 0) {
                initialRole = null;
            } else if (selectableRoles.length === 1) {
                initialRole = selectableRoles[0];
            } else {
                initialRole = Role.TRAINER;
            }

            if (selectableRoles) {
                setAccount(initialAccount ? {...initialAccount} : {
                    firstName: null,
                    lastName: null,
                    email: null,
                    role: initialRole,
                    password: null,
                    club: {id: clubId}
                });
            }
            setRepeatedPassword(undefined);
            setIsValidated(false);
            setAccountAlreadyExistsError(false);
        }
    }, [open]);

    useEffect(() => {
        if (isValidated) {
            validate();
        }
    }, [account, repeatedPassword]);

    const handleChange = (key, value) => {
        const updatedAccount = {...account};
        updatedAccount[key] = value;
        setAccount(updatedAccount);
    };

    const handleSave = () => {
        if (validate()) {
            accountApi.createOrUpdate(account).then(response => {
                if (response.data.success) {
                    setAccountAlreadyExistsError(false);
                    onSave();
                } else {
                    if (response.errorCode === ErrorCode.DATA_ALREADY_EXISTS.code) {
                        setAccountAlreadyExistsError(true);
                    }
                }
            });
        }
    };

    const validate = () => {
        const newValidation = {
            lastName: !!account?.lastName,
            mail: !!account.email && isEmail(account?.email),
            password: !account?.id ? !!account.password : true,
            repeatPassword: account?.password === repeatedPassword || !account?.password && !repeatedPassword
        };
        setIsValidated(true);
        setValidation(newValidation);
        return checkValidation(newValidation);
    };

    if (!account) {
        return null;
    }

    return (
        <Dialog
            open={open}
            title={title}
            footer={
                <>
                    <button
                        onClick={onCancel}
                        type="button"
                        className="btn btn-transparent-primary">
                        <FontAwesomeIcon icon="times" className="mr-2"/>
                        {t('Button.CANCEL')}
                    </button>

                    <button
                        onClick={handleSave}
                        type="button"
                        className="btn btn-primary">
                        <FontAwesomeIcon icon="check" className="mr-2"/>
                        {t('Button.SAVE')}
                    </button>
                </>
            }
        >
            <TextInput
                label={t('Account.FIRST_NAME')}
                value={account.firstName}
                onChange={value => handleChange('firstName', value)}
            />

            <TextInput
                label={t('Account.LAST_NAME')}
                value={account.lastName}
                onChange={value => handleChange('lastName', value)}
                required={isValidated && !validation.lastName}
            />

            <TextInput
                errorMessage={accountAlreadyExistsError ? t('Error.ACCOUNT_ALREADY_EXISTS') : t('Account.VALID_EMAIL')}
                label={t('Account.EMAIL')}
                value={account.email}
                required={accountAlreadyExistsError || isValidated && !validation.mail}
                onChange={value => handleChange('email', value)}
            />

            <TextInput
                label={account?.id ? t('Account.PASSWORD_CHANGE') : t('Account.PASSWORD_NEW')}
                type={'password'}
                required={isValidated && !validation.password}
                value={account.password}
                onChange={value => {
                    handleChange('password', value);
                }}
            />

            <TextInput
                errorMessage={t('Account.PASSWORD_NOT_MATCH')}
                label={t('Account.PASSWORD_REPEAT')}
                type={'password'}
                required={isValidated && !validation.repeatPassword}
                value={repeatedPassword}
                onChange={value => setRepeatedPassword(value)}
            />

            <Dropdown
                options={roles}
                label={t('Account.ROLE')}
                value={{
                    label: t(`Role.${account.role}`),
                    value: account.role
                }}
                onChange={(value) => handleChange('role', value.value)}
                isDisabled={accountId === account.id}
                useRawValue={true}
            />
        </Dialog>
    );
}
